/**
 * Git provider registry.
 *
 * Single source of truth for the providers the app knows about: their ids, labels,
 * how they connect (OAuth redirect vs. credential form), PR/MR terminology, icons,
 * and the optional feature key that gates whether a provider is offered at all.
 *
 * Providers with a `featureKey` are hidden unless `/features/effective` reports that
 * key (see `useEnabledProviders`). Label/icon lookups are intentionally unconditional
 * so repositories already imported from a gated provider keep rendering correctly
 * even if the flag is later switched off.
 */
import React from 'react';
import { Github } from 'lucide-react';

export type GitProvider = 'github' | 'gitlab' | 'azure_devops' | 'codecommit';

export type ProviderConnectKind = 'oauth' | 'form';

export interface GitProviderMeta {
  id: GitProvider;
  label: string;
  /** 'oauth' redirects to the provider; 'form' collects credentials in-app. */
  connectKind: ProviderConnectKind;
  prTerm: 'PR' | 'MR';
  prTermLong: 'pull request' | 'merge request';
  /** Feature key (from /features/effective) required to offer this provider. Unset = always on. */
  featureKey?: string;
}

export const DEFAULT_PROVIDER: GitProvider = 'github';

/** Display / tab order. */
export const GIT_PROVIDERS: readonly GitProvider[] = ['github', 'gitlab', 'azure_devops', 'codecommit'];

export const PROVIDERS: Record<GitProvider, GitProviderMeta> = {
  github: {
    id: 'github',
    label: 'GitHub',
    connectKind: 'oauth',
    prTerm: 'PR',
    prTermLong: 'pull request',
  },
  gitlab: {
    id: 'gitlab',
    label: 'GitLab',
    connectKind: 'oauth',
    prTerm: 'MR',
    prTermLong: 'merge request',
  },
  azure_devops: {
    id: 'azure_devops',
    label: 'Azure DevOps',
    connectKind: 'form',
    prTerm: 'PR',
    prTermLong: 'pull request',
  },
  codecommit: {
    id: 'codecommit',
    label: 'AWS CodeCommit',
    connectKind: 'form',
    prTerm: 'PR',
    prTermLong: 'pull request',
    featureKey: 'codecommit_provider',
  },
};

export const isGitProvider = (value: unknown): value is GitProvider =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(PROVIDERS, value);

/**
 * Coerce a possibly-missing provider string to a known provider. Legacy repositories
 * were created before `provider` existed and are GitHub, which is why the fallback is
 * 'github' (matching the historical `repo.provider || 'github'` idiom).
 */
export const normalizeProvider = (provider?: string | null): GitProvider =>
  isGitProvider(provider) ? provider : DEFAULT_PROVIDER;

export const providerLabel = (provider?: string | null): string =>
  PROVIDERS[normalizeProvider(provider)].label;

export const prTerm = (provider?: string | null): 'PR' | 'MR' =>
  PROVIDERS[normalizeProvider(provider)].prTerm;

export const prTermLong = (provider?: string | null): 'pull request' | 'merge request' =>
  PROVIDERS[normalizeProvider(provider)].prTermLong;

/** True when the provider collects credentials in-app (PAT / access keys) rather than via OAuth. */
export const isFormProvider = (provider?: string | null): boolean =>
  PROVIDERS[normalizeProvider(provider)].connectKind === 'form';

/** Whether a provider should be offered given the current effective feature keys. */
export const isProviderEnabled = (provider: GitProvider, features: readonly string[]): boolean => {
  const key = PROVIDERS[provider].featureKey;
  return !key || features.includes(key);
};

/** Human-readable list, e.g. "GitHub, GitLab, or Azure DevOps". */
export const joinProviderLabels = (providers: readonly GitProvider[]): string => {
  const labels = providers.map((p) => PROVIDERS[p].label);
  if (labels.length <= 1) return labels.join('');
  if (labels.length === 2) return `${labels[0]} or ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, or ${labels[labels.length - 1]}`;
};

/** Message for a 401 from a credential-based provider (PAT / access keys rejected). */
export const credentialsInvalidMessage = (provider?: string | null): string => {
  switch (normalizeProvider(provider)) {
    case 'azure_devops':
      return 'Azure DevOps PAT is invalid or revoked. Reconnect in Organization Settings.';
    case 'codecommit':
      return 'AWS CodeCommit credentials are invalid or revoked. Reconnect in Organization Settings.';
    default:
      return `${providerLabel(provider)} connection is invalid or revoked. Reconnect in Organization Settings.`;
  }
};

/** Best-effort provider inference from a repository / clone / console URL. */
export const inferProviderFromUrl = (url?: string | null): GitProvider | null => {
  if (!url) return null;
  let host = '';
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (host === 'github.com' || host.endsWith('.github.com')) return 'github';
  if (host.startsWith('git-codecommit.') || host.endsWith('console.aws.amazon.com')) return 'codecommit';
  if (host === 'dev.azure.com' || host.endsWith('.visualstudio.com')) return 'azure_devops';
  if (host.includes('gitlab')) return 'gitlab';
  return null;
};

/** Split a CodeCommit `full_name` ("{account_id}/{region}/{repo}") into its parts. */
export const parseCodeCommitFullName = (
  fullName: string
): { accountId: string; region: string; repoName: string } | null => {
  const [accountId, region, ...rest] = fullName.split('/');
  const repoName = rest.join('/');
  if (!accountId || !region || !repoName) return null;
  return { accountId, region, repoName };
};

export interface CommitUrlArgs {
  provider?: string | null;
  repositoryUrl: string;
  fullName?: string;
  sha: string;
}

/**
 * Web URL for a commit. GitHub, GitLab and Azure DevOps all accept `<repo url>/commit/<sha>`;
 * CodeCommit only has a console URL that is derived from the region and repository name.
 */
export const commitUrl = ({ provider, repositoryUrl, fullName, sha }: CommitUrlArgs): string => {
  const resolved = isGitProvider(provider) ? provider : inferProviderFromUrl(repositoryUrl) ?? DEFAULT_PROVIDER;
  if (resolved === 'codecommit' && fullName) {
    const parts = parseCodeCommitFullName(fullName);
    if (parts) {
      const { region, repoName } = parts;
      return `https://${region}.console.aws.amazon.com/codesuite/codecommit/repositories/${encodeURIComponent(
        repoName
      )}/commit/${sha}?region=${region}`;
    }
  }
  return `${repositoryUrl}/commit/${sha}`;
};

interface ProviderIconProps {
  provider?: string | null;
  className?: string;
}

const GitLabIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M23.955 13.587l-1.342-4.135-2.664-8.189a.455.455 0 0 0-.867 0L16.418 9.45H7.582L4.919 1.263a.455.455 0 0 0-.867 0L1.388 9.452.045 13.587a.924.924 0 0 0 .331 1.023L12 23.054l11.624-8.443a.924.924 0 0 0 .331-1.024" />
  </svg>
);

const AzureDevOpsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" fill="currentColor" aria-hidden="true">
    <path d="M120.89 28.445v69.262l-28.445 23.324-44.09-16.07v15.93L23.395 88.25l72.746 5.688V31.574ZM96.64 31.93 55.82 7.11v16.285L18.348 34.418 7.109 48.852v32.785l16.075 7.11V46.718Zm0 0" />
  </svg>
);

/** Simple, brand-neutral glyph for AWS CodeCommit: a commit node on a branch line, boxed. */
const CodeCommitIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <circle cx="12" cy="12" r="2.5" />
    <path d="M12 3v6.5M12 14.5V21" />
  </svg>
);

/** Renders the icon for a provider. Unknown / missing providers render as GitHub (legacy default). */
export const ProviderIcon: React.FC<ProviderIconProps> = ({ provider, className }) => {
  switch (normalizeProvider(provider)) {
    case 'gitlab':
      return <GitLabIcon className={className} />;
    case 'azure_devops':
      return <AzureDevOpsIcon className={className} />;
    case 'codecommit':
      return <CodeCommitIcon className={className} />;
    case 'github':
    default:
      return <Github className={className} />;
  }
};
